import { Mail, MapPin, Phone, Clock3 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, SectionHeading } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import SocialLinks from "@/components/SocialLinks";
import { useSite } from "@/context/SiteContext";
import { waDisplay } from "@/lib/constants";
import CtaBand from "./home/CtaBand";

export default function Contact() {
  const { config, openQuote, whatsappHref } = useSite();
  const waLink = whatsappHref();

  const items = [
    { icon: <Phone className="h-5 w-5" />, label: "TELEFON", value: config.phone, href: config.phone ? `tel:${config.phone.replace(/\s/g, "")}` : null, testid: "contact-phone" },
    { icon: <WhatsAppIcon className="h-5 w-5" />, label: "WHATSAPP", value: waDisplay(config.whatsapp_number) || config.phone, href: waLink, testid: "contact-whatsapp" },
    { icon: <Mail className="h-5 w-5" />, label: "EMAIL", value: config.email, href: config.email ? `mailto:${config.email}` : null, testid: "contact-email" },
    { icon: <MapPin className="h-5 w-5" />, label: "ADRESĂ", value: config.address, href: null, testid: "contact-address" },
    { icon: <Clock3 className="h-5 w-5" />, label: "PROGRAM", value: config.working_hours, href: null, testid: "contact-hours" },
  ];

  return (
    <>
      <SEO
        title="Contact — discută cu noi pe WhatsApp | BROX CONSTRUCT"
        description="Contactează Brox Construct: telefon, WhatsApp, email, adresă și program. Discută cu noi pe WhatsApp despre proiectul tău."
        path="/contact"
      />

      <section className="grain bg-ink-950 pb-10 pt-28 sm:pb-20 sm:pt-40">
        <div className="container-brox">
          <Reveal><p className="eyebrow mb-5">CONTACT</p></Reveal>
          <SectionHeading title={<>Să vorbim despre<br /><span className="text-copper">proiectul tău.</span></>} />
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Cel mai rapid drum către o ofertă este WhatsApp. Ne descrii proiectul, te contactăm în maximum
              24 de ore.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => openQuote({ source: "contact" })} className="btn-primary copper-sheen" data-testid="contact-cta-whatsapp">
                <WhatsAppIcon className="h-5 w-5" />
                CERE OFERTĂ PE WHATSAPP
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <SocialLinks className="mt-10" idPrefix="contact-social" />
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-950 pb-20" data-testid="contact-details-section">
        <div className="container-brox grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.label} delay={0.06 * i} className="bg-ink-900">
              <div className="flex h-full flex-col gap-3 p-6 sm:p-8" data-testid={item.testid}>
                <span className="flex h-11 w-11 items-center justify-center border border-copper/40 text-copper">{item.icon}</span>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{item.label}</p>
                {item.href && item.value ? (
                  <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-base text-paper transition-colors hover:text-copper">
                    {item.value}
                  </a>
                ) : (
                  <p className="whitespace-pre-line text-base text-paper/85">{item.value || "Se actualizează în curând"}</p>
                )}
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3} className="bg-copper">
            <div className="flex h-full flex-col justify-between gap-6 p-7 sm:p-8" data-testid="contact-quote-card">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-950/70">OFERTĂ</p>
                <p className="display-title mt-3 text-3xl text-ink-950">Cere ofertă în 2 minute</p>
                <p className="mt-3 text-sm leading-relaxed text-ink-950/75">
                  Răspunzi la câteva întrebări scurte și ne arăți ce ai nevoie. Te contactăm în maximum 24 de ore.
                </p>
              </div>
              <button onClick={() => openQuote({ source: "contact" })} className="inline-flex min-h-[52px] items-center justify-center gap-2 bg-ink-950 px-6 py-3 font-display text-lg font-bold uppercase tracking-wider text-paper transition-all duration-300 hover:bg-ink-800" data-testid="contact-start-quote-btn">
                <WhatsAppIcon className="h-5 w-5 text-wa" />
                ÎNCEPE CEREREA
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
